import React, { useState, useEffect, useRef } from 'react';
import { X, Save, ImagePlus, Trash2, ExternalLink } from 'lucide-react';
import { useAccounts, useCategories } from '../hooks';
import { Transaction, TransactionWithDetails } from '../lib/services/TransactionService';
import { cleanNumericInput, parseNumericValue, isValidNumericValue } from '../lib/utils/numberUtils';
import {
  uploadTransactionAttachment,
  removeTransactionAttachmentFile,
  getTransactionAttachmentSignedUrl,
} from '../lib/services/transactionAttachmentService';
import { filterCategoriesForTransactionType } from '../lib/transactionCashflow';

interface TransactionFormProps {
  transaction?: Transaction | TransactionWithDetails | null;
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (transaction: Transaction) => Promise<{ success: boolean; error?: string }>;
  /** Requis pour joindre / retirer une image sur une transaction existante (option A) */
  onEditTransaction?: (id: string, updates: Partial<Transaction>) => Promise<{ success: boolean; error?: string }>;
  title?: string;
}

const TransactionForm: React.FC<TransactionFormProps> = ({
  transaction,
  isOpen,
  onClose,
  onSubmit,
  onEditTransaction,
  title = 'Nouvelle transaction'
}) => {
  const { comptes } = useAccounts();
  const { categories } = useCategories();
  
  const [formData, setFormData] = useState({
    description: '',
    montant: '' as string | number,
    type: 'depense' as 'depense' | 'revenu' | 'transfert' | 'pret' | 'remboursement',
    date: new Date().toISOString().split('T')[0],
    compte_id: '',
    categorie_id: '',
    compte_destination_id: undefined as string | undefined,
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [attachmentPath, setAttachmentPath] = useState<string | null>(null);
  const [attachmentPreviewUrl, setAttachmentPreviewUrl] = useState<string | null>(null);
  const [attachmentBusy, setAttachmentBusy] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Réinitialiser les champs « métier » seulement à l’ouverture ou changement de transaction (id), pas quand seule la PJ est syncée
  useEffect(() => {
    if (!isOpen) return;
    if (transaction) {
      setFormData({
        description: transaction.description,
        montant: Math.abs(transaction.montant).toString(),
        type: transaction.type || (transaction.montant < 0 ? 'depense' : 'revenu'),
        date: transaction.date,
        compte_id: transaction.compte_id,
        categorie_id: transaction.categorie_id,
        compte_destination_id: transaction.compte_destination_id,
      });
      setAttachmentPath(transaction.attachment_path ?? null);
    } else {
      setFormData({
        description: '',
        montant: '',
        type: 'depense' as 'depense' | 'revenu' | 'transfert' | 'pret' | 'remboursement',
        date: new Date().toISOString().split('T')[0],
        compte_id: '',
        categorie_id: '',
        compte_destination_id: undefined,
      });
      setAttachmentPath(null);
      setAttachmentPreviewUrl(null);
    }
    setError(null);
  }, [isOpen, transaction?.id]);

  // Même transaction : mise à jour du chemin PJ (ex. après refresh liste) sans écraser la saisie en cours
  useEffect(() => {
    if (!isOpen || !transaction?.id) return;
    setAttachmentPath(transaction.attachment_path ?? null);
  }, [isOpen, transaction?.id, transaction?.attachment_path]);

  useEffect(() => {
    const path = attachmentPath ?? transaction?.attachment_path ?? null;
    if (!path || !isOpen) {
      setAttachmentPreviewUrl(null);
      return;
    }
    let cancelled = false;
    (async () => {
      const { url } = await getTransactionAttachmentSignedUrl(path);
      if (!cancelled && url) {
        setAttachmentPreviewUrl(url);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [attachmentPath, transaction?.attachment_path, transaction?.id, isOpen]);

  const handleInputChange = (field: keyof Transaction, value: any) => {
    setFormData((prev) => {
      if (field === 'type') {
        return {
          ...prev,
          type: value,
          categorie_id: '',
          ...(value !== 'transfert' ? { compte_destination_id: undefined } : {}),
        };
      }
      return {
        ...prev,
        [field]: value,
      };
    });
  };

  const handleAttachmentPick = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !transaction?.id || !onEditTransaction) {
      return;
    }
    setAttachmentBusy(true);
    setError(null);
    try {
      const prev = attachmentPath;
      const { path, error: upErr } = await uploadTransactionAttachment(transaction.id, file);
      if (upErr || !path) {
        setError(upErr || 'Échec de l’envoi du fichier');
        return;
      }
      if (prev) {
        await removeTransactionAttachmentFile(prev);
      }
      const result = await onEditTransaction(transaction.id, { attachment_path: path });
      if (!result.success) {
        await removeTransactionAttachmentFile(path);
        setError(result.error || 'Mise à jour impossible');
        return;
      }
      setAttachmentPath(path);
    } finally {
      setAttachmentBusy(false);
    }
  };

  const handleOpenAttachmentInNewTab = async () => {
    const path = attachmentPath ?? transaction?.attachment_path;
    if (!path) return;
    let url = attachmentPreviewUrl;
    if (!url) {
      const { url: signed } = await getTransactionAttachmentSignedUrl(path);
      if (!signed) return;
      url = signed;
    }
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleRemoveAttachment = async () => {
    if (!transaction?.id || !onEditTransaction || !attachmentPath) {
      return;
    }
    setAttachmentBusy(true);
    setError(null);
    try {
      const path = attachmentPath;
      const result = await onEditTransaction(transaction.id, { attachment_path: null });
      if (!result.success) {
        setError(result.error || 'Suppression impossible');
        return;
      }
      await removeTransactionAttachmentFile(path);
      setAttachmentPath(null);
      setAttachmentPreviewUrl(null);
    } finally {
      setAttachmentBusy(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validation des champs obligatoires
    if (!formData.description?.trim()) {
      setError('La description est obligatoire');
      return;
    }
    
    if (!isValidNumericValue(formData.montant)) {
      setError('Le montant doit être un nombre valide');
      return;
    }
    
    const montantValue = typeof formData.montant === 'string' ? parseNumericValue(formData.montant) : formData.montant;
    
    if (montantValue <= 0) {
      setError('Le montant doit être supérieur à 0');
      return;
    }
    
    if (!formData.compte_id) {
      setError('Veuillez sélectionner un compte');
      return;
    }
    
    if (!formData.categorie_id) {
      setError('Veuillez sélectionner une catégorie');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Préparer les données de la transaction
      // Ne pas envoyer attachment_path ici : l'upload / « Retirer » appellent déjà onEditTransaction.
      // Sinon une 2e édition sans chemin dans la liste (prop obsolète) enverrait null et effacerait la PJ en base.
      const transactionData: Transaction = {
        description: formData.description!,
        montant:
          formData.type === 'depense' || formData.type === 'pret'
            ? -Math.abs(montantValue)
            : Math.abs(montantValue),
        type: formData.type!,
        date: formData.date!,
        compte_id: formData.compte_id!,
        categorie_id: formData.categorie_id!,
        ...(formData.compte_destination_id && { compte_destination_id: formData.compte_destination_id }),
      };

      const result = await onSubmit(transactionData);
      
      if (result.success) {
        onClose();
      } else {
        setError(result.error || 'Erreur lors de la sauvegarde');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur inattendue');
    } finally {
      setLoading(false);
    }
  };

  const filteredCategories = filterCategoriesForTransactionType(categories, formData.type, {
    keepCategoryId: formData.categorie_id || null,
  });

  // Empêcher le scroll de la page quand la modale est ouverte
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/75 flex items-center justify-center z-50 p-4">
      <div className="bg-dark-800 border border-dark-700 rounded-lg w-full max-w-md max-h-[85vh] flex flex-col shadow-xl">
        {/* En-tête */}
        <div className="flex items-center justify-between shrink-0 px-6 pt-6 pb-4">
          <h2 className="text-xl font-bold text-white">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-dark-400 hover:text-white transition-colors"
            aria-label="Fermer"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Formulaire */}
        <form onSubmit={handleSubmit} className="flex flex-col min-h-0 flex-1">
          <div className="min-h-0 flex-1 overflow-y-auto px-6 space-y-4">
          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-white mb-2">
              Description *
            </label>
            <input
              type="text"
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              className="w-full px-3 py-2 bg-dark-800 border border-dark-700 rounded-lg text-white placeholder-dark-400 focus:outline-none focus:border-primary-500"
              placeholder="Ex: Courses Auchan"
              required
            />
          </div>

          {/* Montant et Type */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-white mb-2">
                Montant *
              </label>
              <input
                type="text"
                inputMode="decimal"
                value={formData.montant}
                onChange={(e) => {
                  const cleaned = cleanNumericInput(e.target.value);
                  handleInputChange('montant', cleaned);
                }}
                className="w-full px-3 py-2 bg-dark-800 border border-dark-700 rounded-lg text-white placeholder-dark-400 focus:outline-none focus:border-primary-500"
                placeholder="0.00"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-white mb-2">
                Type *
              </label>
              <select
                value={formData.type}
                onChange={(e) => handleInputChange('type', e.target.value)}
                className="w-full px-3 py-2 bg-dark-800 border border-dark-700 rounded-lg text-white focus:outline-none focus:border-primary-500"
                required
              >
                <option value="depense">Dépense</option>
                <option value="revenu">Revenu</option>
                <option value="transfert">Transfert entre comptes</option>
                <option value="pret">Prêt (sortie d&apos;argent)</option>
                <option value="remboursement">Remboursement reçu</option>
              </select>
              {(formData.type === 'pret' || formData.type === 'remboursement' || formData.type === 'transfert') && (
                <p className="mt-1.5 text-xs text-dark-400">
                  N&apos;apparaît pas dans les totaux Revenus / Dépenses de la Synthèse, mais met à jour le solde.
                </p>
              )}
            </div>
          </div>

          {/* Date */}
          <div>
            <label className="block text-sm font-medium text-white mb-2">
              Date *
            </label>
            <input
              type="date"
              value={formData.date}
              onChange={(e) => handleInputChange('date', e.target.value)}
              className="w-full px-3 py-2 bg-dark-800 border border-dark-700 rounded-lg text-white focus:outline-none focus:border-primary-500"
              required
            />
          </div>

          {/* Compte source */}
          <div>
            <label className="block text-sm font-medium text-white mb-2">
              Compte source *
            </label>
            <select
              value={formData.compte_id}
              onChange={(e) => handleInputChange('compte_id', e.target.value)}
              className="w-full px-3 py-2 bg-dark-800 border border-dark-700 rounded-lg text-white focus:outline-none focus:border-primary-500"
              required
            >
              <option value="">Sélectionner un compte</option>
              {comptes.map((compte) => (
                <option key={compte.id} value={compte.id}>
                  {compte.nom} - {compte.solde.toFixed(2)}€
                </option>
              ))}
            </select>
          </div>

          {/* Compte destination (pour les transferts) */}
          {formData.type === 'transfert' && (
            <div>
              <label className="block text-sm font-medium text-white mb-2">
                Compte destination *
              </label>
              <select
                value={formData.compte_destination_id || ''}
                onChange={(e) => handleInputChange('compte_destination_id', e.target.value || null)}
                className="w-full px-3 py-2 bg-dark-800 border border-dark-700 rounded-lg text-white focus:outline-none focus:border-primary-500"
                required
              >
                <option value="">Sélectionner un compte</option>
                {comptes
                  .filter(compte => compte.id !== formData.compte_id)
                  .map((compte) => (
                    <option key={compte.id} value={compte.id}>
                      {compte.nom} - {compte.solde.toFixed(2)}€
                    </option>
                  ))}
              </select>
            </div>
          )}

          {/* Catégorie */}
          <div>
            <label className="block text-sm font-medium text-white mb-2">
              Catégorie *
            </label>
            <select
              value={formData.categorie_id}
              onChange={(e) => handleInputChange('categorie_id', e.target.value)}
              className="w-full px-3 py-2 bg-dark-800 border border-dark-700 rounded-lg text-white focus:outline-none focus:border-primary-500"
              required
            >
              <option value="">Sélectionner une catégorie</option>
              {filteredCategories.map((categorie) => (
                <option key={categorie.id} value={categorie.id}>
                  {categorie.nom}
                </option>
              ))}
            </select>
          </div>

          {/* Pièce jointe (option A — édition uniquement) */}
          {transaction?.id && onEditTransaction && (
            <div className="border border-dark-600 rounded-lg p-3 space-y-2">
              <label className="block text-sm font-medium text-white">Image (option A — debug Storage)</label>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                className="hidden"
                onChange={handleAttachmentPick}
                disabled={attachmentBusy || loading}
              />
              {attachmentPreviewUrl && (
                <div className="relative rounded-md overflow-hidden border border-dark-600 max-h-40">
                  <img src={attachmentPreviewUrl} alt="" className="w-full object-contain max-h-40 bg-dark-900" />
                </div>
              )}
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={attachmentBusy || loading}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-dark-700 text-white rounded-md hover:bg-dark-600 disabled:opacity-50"
                >
                  <ImagePlus className="w-4 h-4" />
                  {attachmentPath ? 'Remplacer l’image' : 'Ajouter une image'}
                </button>
                {attachmentPath && (
                  <button
                    type="button"
                    onClick={handleOpenAttachmentInNewTab}
                    disabled={attachmentBusy || loading}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-dark-700 text-white rounded-md hover:bg-dark-600 disabled:opacity-50"
                    title="Ouvrir l’image en plein écran dans un nouvel onglet"
                  >
                    <ExternalLink className="w-4 h-4" />
                    Nouvel onglet
                  </button>
                )}
                {attachmentPath && (
                  <button
                    type="button"
                    onClick={handleRemoveAttachment}
                    disabled={attachmentBusy || loading}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-red-500/15 text-red-300 rounded-md hover:bg-red-500/25 disabled:opacity-50"
                  >
                    <Trash2 className="w-4 h-4" />
                    Retirer
                  </button>
                )}
              </div>
              <p className="text-[11px] text-dark-400">
                Test direct Storage + RLS. Si erreur réseau ou 403, passer à l’option B (Edge Function).
              </p>
            </div>
          )}

          {/* Message d'erreur */}
          {error && (
            <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
              <p className="text-red-400 text-sm">{error}</p>
            </div>
          )}
          </div>

          {/* Boutons — toujours visibles */}
          <div className="shrink-0 flex space-x-3 px-6 pt-4 pb-6 border-t border-dark-700">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 bg-dark-800 text-white rounded-lg hover:bg-dark-700 transition-colors"
              disabled={loading}
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Sauvegarde...</span>
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  <span>Sauvegarder</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TransactionForm; 